import { useState } from 'react';
import type { CheckReport } from '@dunea/shared';
import { apiCheckManifest } from '~/lib/api/marketplace';
import { classNames } from '~/utils/classNames';
import { BTN_SECONDARY, ErrorBanner, Spinner } from '~/components/workbench/tools/payments/ui';
import { apiErrorMessage, checkSummary, groupCheckFindings } from '~/components/marketplace/developer-model';

/**
 * "Run checks": the submission rules run on the server (POST dev/check, no side
 * effects), grouped by rule id. `raw` is the parsed manifest; null disables it.
 */
export function ServerCheck({ raw, disabled }: { raw: unknown; disabled?: boolean }) {
  const [report, setReport] = useState<{ for: unknown; data: CheckReport } | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const current = report && report.for === raw ? report.data : null;

  const run = async () => {
    setBusy(true);
    setError(null);

    try {
      setReport({ for: raw, data: await apiCheckManifest(raw) });
    } catch (err) {
      setError(apiErrorMessage(err, 'Could not run the checks.'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-2">
      <button
        type="button"
        onClick={() => void run()}
        disabled={disabled || busy || raw === null}
        className={BTN_SECONDARY}
      >
        {busy ? <Spinner /> : <div className="i-ph:list-checks text-[13px]" />}
        Run review checks
      </button>
      {error && <ErrorBanner message={error} />}
      {current && <CheckReportView report={current} />}
    </div>
  );
}

export function CheckReportView({ report }: { report: CheckReport }) {
  const groups = groupCheckFindings(report);

  return (
    <div
      className={classNames(
        'space-y-2.5 rounded-lg border p-3',
        report.ok ? 'border-success/25 bg-success/[0.04]' : 'border-danger/30 bg-danger/[0.04]',
      )}
      role="status"
    >
      <div className="flex flex-wrap items-center justify-between gap-2">
        <span
          className={classNames(
            'inline-flex items-center gap-1.5 text-[12px] font-medium',
            report.ok ? 'text-success' : 'text-danger',
          )}
        >
          <span className={classNames(report.ok ? 'i-ph:check-circle' : 'i-ph:x-circle', 'text-[13px]')} />
          {checkSummary(report)}
        </span>
        {report.ok && (
          <span className="text-[11px] text-muted">
            {report.fastTrackEligible ? 'Fast track: ' : 'Human review: '}
            {report.fastTrackReason}
          </span>
        )}
      </div>
      {groups.length > 0 && (
        <ul className="space-y-2">
          {groups.map((g) => (
            <li key={`${g.level}:${g.rule}`} className="space-y-0.5">
              <div className="flex flex-wrap items-baseline gap-x-2 text-[11.5px]">
                <code
                  className={classNames(
                    'font-geist-mono text-[10.5px]',
                    g.level === 'error' ? 'text-danger' : 'text-warning',
                  )}
                >
                  {g.rule}
                </code>
                {g.ruleText && <span className="text-secondary">{g.ruleText}</span>}
              </div>
              <ul className="space-y-0.5 pl-3">
                {g.findings.map((f, i) => (
                  <li key={i} className="text-[11.5px] leading-relaxed text-muted">
                    {f.path && <code className="mr-1 font-geist-mono text-[10.5px] text-primary">{f.path}</code>}
                    {f.message}
                  </li>
                ))}
              </ul>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
